import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { booleanScheme, numberScheme } from 'src/utils/validation-schemes';
import { Button } from 'src/components/ui/button';
import {
  useRunKnowledgeBaseCollectorMutation,
  useUpdateKnowledgeBaseCollectorMutation,
} from 'src/services/knowledgeBaseService';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { Switch } from 'src/components/ui/switch';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { WEBSITE_INTERVAL } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';
import { useGetUsersMeQuery } from 'src/services/usersService';
import { ROLES } from 'src/constants/roles';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { useAppSelector } from 'src/store';

export const SyncKnowledgeBaseFreshdeskTicketsForm = ({ item, close = () => {} }) => {
  const { id } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const { data: userInfo } = useGetUsersMeQuery();
  const isCosupportManager = userInfo?.role === ROLES.COSSUPORT_MANAGER;
  const { isTrialPlan } = useAppSelector((s) => s.planSlice);
  const [runCollector, { isLoading: isLoadingRunCollector, isSuccess: isSuccessRunCollector }] =
    useRunKnowledgeBaseCollectorMutation();
  const [
    updateKnowledgeBaseCollectorMutation,
    {
      isLoading: isLoadingUpdateKnowledgeBaseCollector,
      isSuccess: isSuccessUpdateKnowledgeBaseCollector,
    },
  ] = useUpdateKnowledgeBaseCollectorMutation();

  useEffect(() => {
    if (isSuccessRunCollector) {
      notification.success(`${item.name} Resync Started Successfully.`);
      close();
    }
    if (isSuccessUpdateKnowledgeBaseCollector) {
      notification.success(`${item.name} Updated Successfully.`);
      close();
    }
  }, [isSuccessRunCollector, isSuccessUpdateKnowledgeBaseCollector]);

  useEffect(() => {
    if (isLoadingRunCollector || isLoadingUpdateKnowledgeBaseCollector) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingRunCollector, isLoadingUpdateKnowledgeBaseCollector]);

  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      autoSync: item.data.schedule.enabled,
      interval: item.data.schedule.interval,
    },
    resolver: yupResolver(
      object({
        autoSync: booleanScheme({ required: false }),
        interval: numberScheme({ required: false }).label('Interval'),
      }),
    ),
  });

  const submit = (data) => {
    const body = {
      ...item.data,
      schedule: {
        ...item.data.schedule,
        enabled: data.autoSync,
        interval: data.interval,
      },
    };

    updateKnowledgeBaseCollectorMutation({ knowledgeBaseId: id, collectorId: item.id, body });
  };

  const resync = (e) => {
    e.preventDefault();

    runCollector({ knowledgeBaseId: id, collectorId: item.id });
  };

  const isAutoSync = watch('autoSync');

  return (
    <form
      id="sync-new-freshdesk-form"
      onSubmit={handleSubmit(submit)}
      className={'flex flex-col gap-5'}
    >
      <div className={'flex flex-row justify-between gap-5'}>
        <div className={'input-container'}>
          <Controller
            control={control}
            name="autoSync"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <span className="flex flex-row gap-4">
                <span className="flex items-center gap-4">
                  <Switch
                    type="black"
                    checked={value}
                    onCheckedChange={onChange}
                    disabled={isTrialPlan}
                  />
                </span>
                <span
                  className={
                    'text-ebony_opacity_30 font-medium font-jamjuree text-sm flex flex-row items-center gap-2'
                  }
                >
                  Auto-sync
                  {isTrialPlan && (
                    <InputInfoToolTip
                      infoToolTip={{
                        side: 'right',
                        description: <>Unavailable for trial plan</>,
                      }}
                    />
                  )}
                </span>
              </span>
            )}
          />
        </div>
        {isAutoSync && (
          <div className={'input-container'}>
            <Controller
              control={control}
              name="interval"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <RadioGroup
                  onChange={onChange}
                  onValueChange={onChange}
                  defaultValue={value as any}
                  disabled={isTrialPlan}
                  className="flex flex-row gap-5 items-center"
                >
                  <div className="flex items-center space-x-[7px] space-y-0">
                    <RadioGroupItem value={WEBSITE_INTERVAL.WEEKLY as any} id={'interval-weekly'} />
                    <label htmlFor={'interval-weekly'} className="cursor-pointer">
                      <p className="font-medium text-sm text-ebony opacity-50 hover:opacity-100 transition">
                        Weekly
                      </p>
                    </label>
                  </div>
                  <div className="flex items-center space-x-[7px] space-y-0">
                    <RadioGroupItem
                      value={WEBSITE_INTERVAL.MONTHLY as any}
                      id={'interval-monthly'}
                    />
                    <label htmlFor={'interval-monthly'} className="cursor-pointer">
                      <p className="font-medium text-sm text-ebony opacity-50 hover:opacity-100 transition">
                        Monthly
                      </p>
                    </label>
                  </div>
                </RadioGroup>
              )}
            />
          </div>
        )}
      </div>
      <div className="flex justify-center mt-5">
        <div className="flex gap-2.5">
          <Button variant="outline" type="button" className="min-w-24" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" className="min-w-24" disabled={!isDirty}>
            <span>Update</span>
          </Button>
          {isCosupportManager && (
            <Button onClick={resync} variant="outline-light" className="min-w-24">
              <span>One-time resync</span>
            </Button>
          )}
        </div>
      </div>
    </form>
  );
};
